import { isEqual } from "lodash"
import { Icon } from '@chakra-ui/icons'
import { Button, Grid, GridItem, Text } from '@chakra-ui/react'
import type { ReactElement } from 'react'
import { useCallback, useEffect, useState } from 'react'
import {
  Fragment,
  useContext,
} from 'react'
import { useTranslation } from 'react-i18next'
import { FiChevronDown } from 'react-icons/fi'
import { FeaturesEnum } from '../../../../../utils/features'
import { foldersPerPage } from '../../../../../utils/mediaLibrary'
import { addLeadingSlash, addTrailingSlash } from '../../../../../utils/urlHelpers'
import useKeyboardSelection from '../../../../hooks/useKeyboardSelection'
import { MLContext, UserContext } from '../../../../providers'
import { MediaLibraryActions } from '../../../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../../../providers/PublicLinksInfoProvider'
import useToast from '../../../../hooks/useToast'
import FolderElement from './FolderElement'
import FolderGridLoader from "./FolderGridLoader"

interface Props {
  loadComplete: { files: boolean; folders: boolean }
  setOnLoadComplete: (type: "folder") => void
}

const FoldersGrid = (
  { loadComplete, setOnLoadComplete }: Props
): ReactElement | null => {
  const { isPublicLink, urlPrefix } = useContext(PublicLinksInfoContext)
  const { t } = useTranslation()
  const toast = useToast()
  const [
    {
      folderList,
      selectedFolders,
      folderPage,
      loadingFolders,
      abortFoldersRequest,
      urlState,
      folderPath,
      queryParams,
      currentMediaCollection,
      mediaCollectionRoute
    },
    dispatch,
  ] = useContext(MLContext)

  const { user } = useContext(UserContext)

  const [gridData, setGridData] = useState({
    folderPage,
    loadingFolders: false,
    folderList,
  })

  const { onSelect } = useKeyboardSelection({
    type: 'folder',
    dispatch,
  })

  // to save folder data comming from MLProvider in gridData.folderList
  useEffect(() => {
    setGridData(prev => ({ ...prev, folderList, folderPage }))
  }, [folderPage, loadingFolders, folderList])

  const generateUrl = useCallback((): string => {
    const params: Record<string, string> = {
      skip: (gridData.folderPage * foldersPerPage).toString(),
      limit: foldersPerPage.toString(),
      sort: urlState.sort,
      searchQuery: queryParams?.searchQuery
        ? `(type="folder") AND ${queryParams.searchQuery}`
        : 'type="folder"',
    }
    const path = queryParams?.searchQuery === '' ? folderPath : null
    if (path !== null) {
      params.path = path
    }

    const searchParams = new URLSearchParams(params)

    return `?${searchParams.toString()}`
  }, [gridData?.folderPage, foldersPerPage, urlState, queryParams, folderPath])

  const loadData = useCallback(() => {
    const collectionId =
      queryParams?.searchQuery === ''
        ? urlState.currentMediaCollection
          ? urlState.currentMediaCollection
          : undefined
        : undefined
    let endpoint

    if (typeof collectionId === 'string') {
      endpoint = `${urlPrefix}api/v2/media-collections/${collectionId}/assets${generateUrl()}`
    } else {
      endpoint = `${urlPrefix}api/v2/files${generateUrl()}`
    }

    if (
      !isPublicLink &&
      endpoint?.startsWith(`${urlPrefix}api/v2/media-collections`) &&
      !user?.pricingPlan.featuresList.includes(FeaturesEnum.ACL_AND_COLLECTIONS)
    )
      return null

    fetch(endpoint)
      .then(response => response.json())
      .then(data => {
        if (!Array.isArray(data)) {
          throw data
        }
        dispatch([{
          type: MediaLibraryActions.SET_FOLDER_LIST,
          payload: [...gridData?.folderList, ...data],
        },
        {
          type: MediaLibraryActions.LOAD_MORE_FOLDERS,
        }])
        setGridData(prev => ({
          ...prev,
          loadingFolders: false,
          folderList: [...prev?.folderList, ...data],
        }))
      })
      .catch(error => {
        toast({
          description: 'Something went wrong!',
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        dispatch({
          type: MediaLibraryActions.SET_FOLDER_LIST,
          payload: [],
        })
        setGridData(prev => ({
          ...prev,
          loadingFolders: false,
          folderList: [],
        }))
      })
  }, [
    generateUrl,
    queryParams,
    gridData?.folderList,
    urlState,
    dispatch,
    setGridData,
    user,
    FeaturesEnum
  ])

  const isFolderSelected = useCallback(
    folder => selectedFolders.map(x => x.folderId).includes(folder?.folderId),
    [selectedFolders],
  )

  useEffect(() => {
    if (gridData?.folderPage && folderPage !== gridData?.folderPage) loadData()
  }, [gridData?.folderPage, folderPage])

  useEffect(() => {
    if (isEqual(gridData?.folderList, folderList)) {
      setOnLoadComplete("folder")
    }
  }, [gridData?.folderList, folderList])

  if (!(loadComplete.files && loadComplete.folders)) {
    return (<Fragment>
      <Text fontWeight="semibold" color="appSlate">
        {t('folders', 'Folders')}
      </Text>
      <FolderGridLoader />
    </Fragment>)

  }

  if (gridData?.folderList.length === 0 && !gridData?.loadingFolders) {
    return null
  }

  return (
    <Fragment>
      <Text fontWeight="semibold" color="appSlate">
        {t('folders', 'Folders')}
      </Text>
      <Grid
        gridTemplateColumns={[
          'repeat(1, minmax(4px, 1fr))',
          'repeat(2, minmax(4px, 1fr))',
          'repeat(3, minmax(4px, 1fr))',
          'repeat(5, minmax(4px, 1fr))',
        ]}
        columnGap={6}
        rowGap={6}
        width="100%"
      >
        {gridData?.folderList.map((x, idx) => {
          const isFolderSelectedValue = isFolderSelected(x)
          return (
            <GridItem
              key={x.folderId}
              // data-id and className are needed to so that the selected
              // ids can be identified in MouseDragSelect component
              data-id={`folder-${x.folderId}`}
              className="mouse-select__selectable"
            >
              <FolderElement
                {...x}
                idx={idx}
                onSelect={onSelect}
                isFolderSelected={isFolderSelectedValue}
                dispatch={dispatch}
                currentMediaCollection={currentMediaCollection}
                mediaCollectionRoute={mediaCollectionRoute}
              />
            </GridItem>
          )
        })}
      </Grid>
      {(gridData?.folderList.length % foldersPerPage === 0 &&
        gridData?.folderList.length / foldersPerPage ===
        gridData?.folderPage + 1) ||
        gridData?.loadingFolders ? (
        <Button
          as="a"
          variant="link"
          cursor="pointer"
          color="appClearBlue"
          onClick={() => {
            abortFoldersRequest()
            setGridData(prev => ({
              ...prev,
              folderPage: prev?.folderPage + 1,
              loadingFolders: true,
            }))
          }}
          isLoading={gridData?.loadingFolders}
          loadingText={t('loading', 'Loading...')}
        >
          {t('see_more', 'See More')}
          <Icon as={FiChevronDown} />
        </Button>
      ) : null}
    </Fragment>
  )
}

export default FoldersGrid
